/**
 * Curated model registry — the single source of truth for which LLM models
 * the platform supports and how their usage maps to Relay Credits.
 *
 * Used by:
 *   - Frontend: agent-create / agent-edit dropdown options.
 *   - Backend: server-side validation of `agents.config.model` on
 *     create/update, and credit-multiplier lookup at cost-event time.
 *
 * The credits-model decision (`docs/MONETIZATION_ROADMAP.md`, 2026-05-08)
 * locked the supported provider/model surface to this list. Adding a new
 * provider or model is a deliberate decision (per CLAUDE.md "ask before
 * adding a vendor"), not a user-driven action.
 *
 * **1 Relay Credit ≈ 1 Sonnet token at the 1× reference rate.** Multipliers
 * govern actual provider-token consumption per credit spent.
 */
import { z } from "zod";
export const ProviderSchema = z.enum(["anthropic", "openai"]);
/**
 * Cost tier groups models by relative price within a provider. Drives UI
 * affordances (badges, sort order in the dropdown) — does not affect billing
 * directly. The numeric `multiplier` is what the credit math reads.
 */
export const CostTierSchema = z.enum(["cheap", "reference", "premium"]);
export const ModelEntrySchema = z.object({
    provider: ProviderSchema,
    modelId: z.string().min(1),
    displayName: z.string().min(1),
    multiplier: z.number().positive(),
    costTier: CostTierSchema,
});
/**
 * The v1 curated set. Six models across two providers, three cost tiers.
 * Multipliers are tunable post-launch — they're data, not code.
 */
export const MODELS = Object.freeze([
    {
        provider: "anthropic",
        modelId: "claude-haiku-4-5",
        displayName: "Claude Haiku 4.5",
        multiplier: 0.5,
        costTier: "cheap",
    },
    {
        provider: "anthropic",
        modelId: "claude-sonnet-4-6",
        displayName: "Claude Sonnet 4.6",
        multiplier: 1,
        costTier: "reference",
    },
    {
        provider: "anthropic",
        modelId: "claude-opus-4-7",
        displayName: "Claude Opus 4.7",
        multiplier: 4,
        costTier: "premium",
    },
    {
        provider: "openai",
        modelId: "gpt-5-4-mini",
        displayName: "GPT-5.4 mini",
        multiplier: 0.5,
        costTier: "cheap",
    },
    {
        provider: "openai",
        modelId: "gpt-5-4",
        displayName: "GPT-5.4",
        multiplier: 1,
        costTier: "reference",
    },
    {
        provider: "openai",
        modelId: "gpt-5-5",
        displayName: "GPT-5.5",
        multiplier: 3,
        costTier: "premium",
    },
]);
const MODEL_MAP = new Map(MODELS.map((m) => [m.modelId, m]));
/** Resolve a model entry by its `modelId`, or `undefined` if not in the registry. */
export function resolveModel(modelId) {
    return MODEL_MAP.get(modelId);
}
/**
 * Resolve a model's credit multiplier. Throws when the modelId is not in the
 * registry — an unknown model in production is a programming error (server-
 * side validation should reject it before this point).
 */
export function resolveModelMultiplier(modelId) {
    const entry = MODEL_MAP.get(modelId);
    if (!entry) {
        throw new Error(`Unknown model: ${modelId}. Not in the curated registry.`);
    }
    return entry.multiplier;
}
/** Whether a `modelId` exists in the curated registry. */
export function isModelInRegistry(modelId) {
    return MODEL_MAP.has(modelId);
}
/** All `modelId`s in the registry, in declaration order. */
export const REGISTERED_MODEL_IDS = MODELS.map((m) => m.modelId);
/**
 * A Zod schema that validates a string against the registered model IDs.
 * Use this when persisting or accepting a model selection at an API boundary
 * to enforce the curated-list constraint.
 */
export const RegistryModelIdSchema = z
    .string()
    .min(1)
    .refine(isModelInRegistry, {
    message: "Model is not in the curated registry",
});
/**
 * The default model surfaced in the agent-create UI when the user hasn't
 * picked one. Sonnet 4.6 sits at the 1× reference multiplier — sensible
 * starting point on the cost/quality curve.
 */
export const DEFAULT_MODEL_ID = "claude-sonnet-4-6";
/**
 * Auto-upgrade map for legacy stored model strings.
 *
 * Tony decided 2026-05-08 (option A): existing agents in the DB whose model
 * predates the curated registry are silently upgraded to the closest current
 * equivalent at the orchestrator dispatch boundary. The user does not need
 * to re-edit the agent for it to keep running.
 *
 * Add an entry here only when an old model string is known to be in stored
 * `agents.config.model` rows. The orchestrator calls `resolveModelOrAlias`
 * before passing the config to the executor — keeping this map small and
 * explicit so it's clear what's being remapped.
 *
 * A separate follow-up migration rewrites `agents.config.model` and
 * `agent_versions.config.model` in-place so the alias map can eventually be
 * retired.
 */
export const LEGACY_MODEL_ALIASES = new Map([
    ["claude-sonnet-4-5", "claude-sonnet-4-6"],
    ["claude-3-5-sonnet-20241022", "claude-sonnet-4-6"],
    ["claude-3-5-haiku-20241022", "claude-haiku-4-5"],
]);
/**
 * Resolve a stored model string into its current registry `modelId`.
 *
 * - If the input is already in the registry, returns it unchanged.
 * - If the input is a known legacy alias, returns the upgraded ID.
 * - Otherwise returns the input unchanged (caller decides whether unknown
 *   models are an error).
 *
 * Pure function — no I/O, safe to call anywhere.
 */
export function resolveModelOrAlias(modelId) {
    if (MODEL_MAP.has(modelId))
        return modelId;
    return LEGACY_MODEL_ALIASES.get(modelId) ?? modelId;
}
/**
 * Group the registry by provider for UI rendering. Order within each group
 * matches declaration order in `MODELS`.
 */
export function modelsByProvider() {
    const grouped = new Map();
    for (const m of MODELS) {
        const arr = grouped.get(m.provider);
        if (arr) {
            arr.push(m);
        }
        else {
            grouped.set(m.provider, [m]);
        }
    }
    return grouped;
}
//# sourceMappingURL=models.js.map